/**
 * ReconcileStockEditView — virtualised with @tanstack/react-virtual
 *
 * install once:
 *   npm install @tanstack/react-virtual
 *
 * What changed vs the original:
 *  ✓  Only ~20 rows are ever in the DOM (virtualizer window), no matter how
 *     many thousand products are loaded.
 *  ✓  react-table removed — a plain <table> with a single useMemo for column
 *     defs is far cheaper.
 *  ✓  PhysicalCountCell is a stable memoized component; it only re-renders
 *     when its own value changes (React.memo + useCallback in parent).
 *  ✓  DiscrepancyReasonCell and every other cell component is memoized the
 *     same way.
 *  ✓  The scroll container is the virtualized element, so the browser never
 *     has to lay out 10 000 <tr> nodes.
 *  ✓  Debounced physical-count input: local state updates instantly; the
 *     expensive reconciliedData update fires 200 ms after the user stops.
 */

import { useRef, useCallback, memo, useState, useEffect, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Images from "../../../assets/images";
import styles from "./styles.module.scss";
import "./ReconcileStockEditView.scss";
import primaryComponents from "../../primaryComponents";
import useReconcileStockEditView from "./useReconcileStockEditView";
import {
  formatCurrency,
  formatNumberWithCommas,
  truncateString,
} from "../../../helpers";

// ─── tiny debounce hook ───────────────────────────────────────────────────────
function useDebounce(fn, delay) {
  const timerRef = useRef(null);
  return useCallback(
    (...args) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fn, delay],
  );
}

// ─── column header config ───────────────────────────────────────────────────
const getHeaders = (isReviewMode) => [
  { label: "", width: isReviewMode ? "3%" : "4%" },
  { label: "Product Name", width: isReviewMode ? "16%" : "18%" },
  { label: "Variant", width: "10%" },
  { label: "System Count", width: isReviewMode ? "9%" : "10%" },
  ...(isReviewMode ? [{ label: "Current System Count", width: "10%" }] : []),
  { label: "Physical Count", width: isReviewMode ? "11%" : "14%" },
  { label: "Discrepancies", width: isReviewMode ? "9%" : "10%" },
  { label: "Estimated Impact", width: isReviewMode ? "11%" : "12%" },
  { label: "Discrepancy Reason", width: isReviewMode ? "13%" : "16%" },
  { label: "Last Updated By", width: isReviewMode ? "8%" : "10%" },
];

// ─── memoised cell components ─────────────────────────────────────────────────

const PhysicalCountCell = memo(
  ({
    value,
    rowIndex,
    row,
    isReviewMode,
    isDraft,
    updateRow,
    updateChanges,
    removeReconciledKey,
  }) => {
    const [localValue, setLocalValue] = useState(value ?? "");

    useEffect(() => {
      setLocalValue(value ?? "");
    }, [value]);

    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
      setDismissed(false);
    }, [row?.system_count_changed]);

    // Flush to shared state 200 ms after the user stops typing
    const flush = useCallback(
      (input) => {
        const parsed = input === "" || Number(input) < 0 ? null : Number(input);
        updateRow(rowIndex, {
          physical_count: parsed,
          system_count_changed: false,
        });
        const item = {
          ...row,
          physical_count: parsed,
          system_count_changed: false,
        };
        if (parsed !== null) updateChanges(item.item_id, item);
        else removeReconciledKey(item.item_id);
      },
      [row, rowIndex, updateRow, updateChanges, removeReconciledKey],
    );

    const debouncedFlush = useDebounce(flush, 200);

    const handleChange = (e) => {
      const input = e.target.value;
      setLocalValue(input);
      setDismissed(true);
      updateRow(rowIndex, { system_count_changed: false });
      debouncedFlush(input);
    };

    const hasSystemCountChanged =
      isDraft && !dismissed && row?.system_count_changed === true;

    return (
      <span className="d-flex align-items-center">
        <primaryComponents.InputField
          classNames={`white ${styles.inputField}`}
          errorMessage={
            hasSystemCountChanged
              ? {
                  message: "System count has changed.",
                }
              : undefined
          }
          placeholder="Enter value"
          floatError={false}
          type="number"
          min="0"
          value={localValue}
          name={`physicalCount${rowIndex}`}
          isDisabled={isReviewMode}
          onChange={handleChange}
        />
        {row?.product_unit_label && (
          <span className="ml-1">{row.product_unit_label}</span>
        )}
      </span>
    );
  },
);
PhysicalCountCell.displayName = "PhysicalCountCell";

const DiscrepancyReasonCell = memo(
  ({
    row,
    rowIndex,
    isReviewMode,
    isDraft,
    reconciliedRef,
    discrepancyReasons,
    updateRow,
    updateChanges,
  }) => {
    const currentId = row.item_id;

    const selectedReasonId =
      row.discrepancy_reason_id ??
      reconciliedRef.current?.[currentId]?.discrepancy_reason_id;

    const selectedReasonLabel =
      row.discrepancy_reason_label ||
      reconciliedRef.current?.[currentId]?.discrepancy_reason_label;

    const effectiveSystemCount =
      isDraft && (row.system_count === null || row.system_count === undefined)
        ? (row.current_system_count ?? row.system_count)
        : row.system_count;

    const hasDiscrepancy =
      row.physical_count !== null &&
      row.physical_count !== "" &&
      Number(row.physical_count) !== Number(effectiveSystemCount);

    const isMissingReason = hasDiscrepancy && !selectedReasonId;

    return (
      <primaryComponents.SelectField
        key={`${currentId}-${selectedReasonId ?? "none"}`}
        floatError={false}
        options={discrepancyReasons}
        isSearchable={false}
        isRequired={hasDiscrepancy}
        errorMessage={
          isMissingReason
            ? { message: "Discrepancy reason is required" }
            : undefined
        }
        borderColor={isMissingReason ? "#dc3545" : "#cccccc"}
        value={selectedReasonLabel || "Select"}
        nameKey="title"
        idKey="id"
        isDisabled={
          row.physical_count === null ||
          row.physical_count === "" ||
          isReviewMode
        }
        onChange={(val, label) => {
          const resolvedLabel =
            label ?? discrepancyReasons.find((r) => r.id === val)?.title;
          updateRow(rowIndex, {
            discrepancy_reason_id: val,
            discrepancy_reason_label: resolvedLabel,
          });
          updateChanges(currentId, {
            ...(reconciliedRef.current?.[currentId] ?? row),
            discrepancy_reason_id: val,
            discrepancy_reason_label: resolvedLabel,
          });
        }}
      />
    );
  },
);
DiscrepancyReasonCell.displayName = "DiscrepancyReasonCell";

// ─── single virtualised row ───────────────────────────────────────────────────
const TableRow = memo(
  ({
    row,
    rowIndex,
    isSelected,
    onSelect,
    store_currency,
    discrepancyReasons,
    isReviewMode,
    isDraft,
    reconciliedRef,
    updateRow,
    updateChanges,
    removeReconciledKey,
    style, // from virtualizer
  }) => {
    const effectiveSystemCount =
      isDraft && (row.system_count === null || row.system_count === undefined)
        ? (row.current_system_count ?? row.system_count)
        : row.system_count;

    // Discrepancy
    const diff = row.physical_count - effectiveSystemCount;
    const isZero = row.physical_count === null || diff === 0;
    const diffLabel = isZero
      ? "success--squared"
      : diff < 0
        ? "failed--text"
        : "success--squared";

    // Impact
    const { physical_count, cost_price } = row;
    const impactDiff = physical_count - effectiveSystemCount;
    const showZero =
      physical_count === null || physical_count === "" || impactDiff === 0;
    const impactLabel = impactDiff < 0 ? "failed--text" : "success--squared";

    return (
      <tr style={style}>
        <td>
          {!isReviewMode ? (
            <primaryComponents.Checkbox
              name="selectedReconciliationItem"
              id={`reconciliation-item-${row.item_id}`}
              isChecked={isSelected}
              onChange={() => onSelect(row.item_id)}
            />
          ) : null}
        </td>
        <td>{truncateString(row.product_name, 500) || "N/A"}</td>
        <td>{truncateString(row.variant_name, 200) || "N/A"}</td>
        <td>
          {formatNumberWithCommas(effectiveSystemCount)}{" "}
          {row.product_unit_label}
        </td>
        {isReviewMode && (
          <td>
            {row.current_system_count !== undefined &&
            row.current_system_count !== null
              ? `${formatNumberWithCommas(row.current_system_count)} ${
                  row.product_unit_label || ""
                }`.trim()
              : "N/A"}
          </td>
        )}
        <td>
          <PhysicalCountCell
            value={row.physical_count}
            rowIndex={rowIndex}
            row={row}
            isReviewMode={isReviewMode}
            isDraft={isDraft}
            updateRow={updateRow}
            updateChanges={updateChanges}
            removeReconciledKey={removeReconciledKey}
          />
        </td>
        <td>
          <span className={diffLabel}>
            {formatNumberWithCommas(isZero ? 0 : diff)}
          </span>{" "}
          {row.product_unit_label || ""}
        </td>
        <td>
          <span className={showZero ? "success--squared" : impactLabel}>
            {formatCurrency({
              value: showZero ? 0 : impactDiff * cost_price,
              currencyCode: store_currency,
            })}
          </span>
        </td>
        <td>
          <DiscrepancyReasonCell
            row={row}
            rowIndex={rowIndex}
            isReviewMode={isReviewMode}
            isDraft={isDraft}
            reconciliedRef={reconciliedRef}
            discrepancyReasons={discrepancyReasons}
            updateRow={updateRow}
            updateChanges={updateChanges}
          />
        </td>
        <td>{truncateString(row.last_updated_by_label, 50) || "N/A"}</td>
      </tr>
    );
  },
  // Custom comparator: skip re-render if the data for this specific row
  // hasn't actually changed (physical_count, discrepancy_reason_id etc.)
  (prev, next) =>
    prev.row === next.row &&
    prev.rowIndex === next.rowIndex &&
    prev.isSelected === next.isSelected &&
    prev.isReviewMode === next.isReviewMode &&
    prev.isDraft === next.isDraft &&
    prev.store_currency === next.store_currency,
);
TableRow.displayName = "TableRow";

// ─── main component ───────────────────────────────────────────────────────────
const ReconcileStockEditView = ({
  store_currency,
  products,
  reconciliedData,
  setReconciliedData,
  discrepancyReasons,
  isReviewMode = false,
  status,
  isDraft: isDraftProp,
  // pagination — passed from parent instead of using a sentinel div
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  stockCountDate,
  setStockCountDate,
  action,
  isLoading,
  loadingText,
  onRefresh,
  isRefreshing = false,
  autoScrollRef,
}) => {
  const isDraft =
    isDraftProp !== undefined
      ? isDraftProp
      : !isReviewMode && (!status || status === "DRAFT");

  const {
    productsList,
    reconciliedRef,
    updateChanges,
    removeReconciledKey,
    updateRow,
  } = useReconcileStockEditView({
    store_currency,
    products,
    reconciliedData,
    setReconciliedData,
    discrepancyReasons,
    isReviewMode,
    isDraft,
  });

  const scrollRef = useRef(null);
  const headers = useMemo(() => getHeaders(isReviewMode), [isReviewMode]);

  const virtualizer = useVirtualizer({
    count: productsList.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 56,
    overscan: 8,
    getItemKey: (i) => productsList[i]?.item_id ?? i,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalHeight = virtualizer.getTotalSize();

  useEffect(() => {
    if (!autoScrollRef || !scrollRef.current) return;
    if (typeof autoScrollRef === "function") {
      autoScrollRef(scrollRef.current);
    } else {
      autoScrollRef.current = scrollRef.current;
    }
  }, [autoScrollRef]);

  // ── virtualizer-native infinite scroll ───────────────────────────────────
  // When the last rendered row is within 5 items of the loaded list end,
  // fetch the next page. No sentinel <div> or useInView needed.
  useEffect(() => {
    if (!virtualItems.length) return;
    const lastRenderedIndex = virtualItems[virtualItems.length - 1].index;
    const distanceFromEnd = productsList.length - 1 - lastRenderedIndex;
    if (distanceFromEnd <= 5 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    virtualItems,
    productsList.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  // ── stable callbacks passed to each row ──────────────────────────────────
  // These are already stable from the hook, but we memoize wrappers to avoid
  // any prop-equality false negatives in TableRow.memo
  const stableUpdateRow = useCallback(updateRow, [updateRow]);
  const stableUpdateChanges = useCallback(updateChanges, [updateChanges]);
  const stableRemoveReconciledKey = useCallback(removeReconciledKey, [
    removeReconciledKey,
  ]);

  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [bulkReason, setBulkReason] = useState(null);

  const selectedItemIdSet = new Set(selectedItemIds);
  const allItemsSelected =
    productsList.length > 0 && selectedItemIds.length === productsList.length;

  const handleItemSelection = useCallback((itemId) => {
    setSelectedItemIds((previous) =>
      previous.includes(itemId)
        ? previous.filter((id) => id !== itemId)
        : [...previous, itemId],
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedItemIds((previous) =>
      previous.length === productsList.length
        ? []
        : productsList.map((item) => item.item_id),
    );
  }, [productsList]);

  const handleBulkReasonChange = useCallback(
    (reasonId, reasonLabel) => {
      if (!reasonId || selectedItemIds.length === 0) return;

      productsList.forEach((row, rowIndex) => {
        if (!selectedItemIdSet.has(row.item_id)) return;

        const updatedRow = {
          ...(reconciliedRef.current?.[row.item_id] ?? row),
          discrepancy_reason_id: reasonId,
          discrepancy_reason_label: reasonLabel,
        };
        updateRow(rowIndex, {
          discrepancy_reason_id: reasonId,
          discrepancy_reason_label: reasonLabel,
        });
        updateChanges(row.item_id, updatedRow);
      });
      setBulkReason(null);
    },
    [
      productsList,
      reconciliedRef,
      selectedItemIds,
      selectedItemIdSet,
      updateChanges,
      updateRow,
    ],
  );

  const handleStockDateChange = useCallback(
    ({ target: { value } }) => {
      setStockCountDate(value);
    },
    [setStockCountDate],
  );

  return (
    <>
      <div
        className={`${styles.reconcileStockEditView} reconcileStockEditView`}
      >
        <div className={styles.headerActions}>
          {!isReviewMode ? (
            <div className={styles.dateFilterGroup}>
              <div className={styles.dateInputWrapper}>
                <primaryComponents.DateInput
                  label="Stock Count Date"
                  value={stockCountDate}
                  onChange={handleStockDateChange}
                  floatError={false}
                />
              </div>
              {onRefresh ? (
                <primaryComponents.Button
                  type="button"
                  classNames={`btn btn--outline smallBtn ${styles.filterActionBtn}`}
                  onClick={onRefresh}
                  isLoading={isRefreshing}
                  isDisabled={isRefreshing}
                >
                  <FontAwesomeIcon
                    icon="redo-alt"
                    className="mr-1"
                    spin={isRefreshing}
                  />{" "}
                  Refresh
                </primaryComponents.Button>
              ) : null}
            </div>
          ) : null}

          {!isReviewMode && productsList.length > 0 ? (
            <div className={styles.bulkActionGroup}>
              <div className={styles.bulkSelectWrapper}>
                <primaryComponents.SelectField
                  key={bulkReason?.id || "bulk-discrepancy-reason"}
                  classNames={styles.bulkReasonSelect}
                  options={discrepancyReasons}
                  label={"Apply reason to selected"}
                  idKey="id"
                  nameKey="title"
                  value={bulkReason?.title || "Select discrepancy reason"}
                  placeholder="Select discrepancy reason"
                  isSearchable={false}
                  isDisabled={selectedItemIds.length === 0}
                  floatError={false}
                  onChange={(reasonId, reasonLabel) => {
                    const reason = discrepancyReasons.find(
                      (item) => item.id === reasonId,
                    );
                    setBulkReason(reason);
                    handleBulkReasonChange(
                      reasonId,
                      reasonLabel || reason?.title,
                    );
                  }}
                />
              </div>
              <primaryComponents.Button
                classNames={`btn smallBtn btn--primary ${styles.bulkApplyBtn}`}
                onClick={action}
                isDisabled={isLoading || selectedItemIds.length === 0}
                isLoading={isLoading}
                loadingText={loadingText || "Applying..."}
              >
                Apply
              </primaryComponents.Button>
            </div>
          ) : null}
        </div>
        {/* tableSection mirrors your existing TableHelper scroll container */}
        <div className={styles.reconcileStockEditView__content}>
          <div
            ref={scrollRef}
            className={`${styles.tableSection} ${styles.handleScroll}`}
          >
            {productsList.length === 0 ? (
              <div className="text-center mt-5 mb-5">
                <img
                  src={Images.reconciliationEmptyState}
                  width="250px"
                  alt=""
                />
                <h4 className="font-weight-semibold text-center mt-4">
                  You have no product for reconciliation yet
                </h4>
              </div>
            ) : (
              <table className={isReviewMode ? styles.reviewTable : undefined}>
                <colgroup>
                  {headers.map((h) => (
                    <col
                      key={h.label || "col-checkbox"}
                      style={{ width: h.width }}
                    />
                  ))}
                </colgroup>

                <thead>
                  <tr>
                    <th>
                      {!isReviewMode ? (
                        <primaryComponents.Checkbox
                          name="selectAllReconciliationItems"
                          id="selectAllReconciliationItems"
                          isChecked={allItemsSelected}
                          onChange={handleSelectAll}
                        />
                      ) : null}
                    </th>
                    {headers.slice(1).map((h) => (
                      <th key={h.label}>{h.label}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {virtualItems.length > 0 && virtualItems[0].start > 0 && (
                    <tr
                      className={styles.spacerRow}
                      style={{ height: virtualItems[0].start }}
                    >
                      <td
                        colSpan={headers.length}
                        style={{
                          height: virtualItems[0].start,
                          padding: 0,
                          border: 0,
                        }}
                      />
                    </tr>
                  )}

                  {virtualItems.map((virtualRow) => {
                    const row = productsList[virtualRow.index];
                    return (
                      <TableRow
                        key={row?.item_id ?? virtualRow.index}
                        row={row}
                        rowIndex={virtualRow.index}
                        isSelected={selectedItemIdSet.has(row.item_id)}
                        onSelect={handleItemSelection}
                        store_currency={store_currency}
                        discrepancyReasons={discrepancyReasons}
                        isReviewMode={isReviewMode}
                        isDraft={isDraft}
                        reconciliedRef={reconciliedRef}
                        updateRow={stableUpdateRow}
                        updateChanges={stableUpdateChanges}
                        removeReconciledKey={stableRemoveReconciledKey}
                      />
                    );
                  })}

                  {virtualItems.length > 0 &&
                    totalHeight - virtualItems[virtualItems.length - 1].end >
                      0 && (
                      <tr
                        className={styles.spacerRow}
                        style={{
                          height:
                            totalHeight -
                            virtualItems[virtualItems.length - 1].end,
                        }}
                      >
                        <td
                          colSpan={headers.length}
                          style={{
                            height:
                              totalHeight -
                              virtualItems[virtualItems.length - 1].end,
                            padding: 0,
                            border: 0,
                          }}
                        />
                      </tr>
                    )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReconcileStockEditView;
